/**
 * customer-service-search.ts
 *
 * Single source of truth for the searchable fields on a CustomerService.
 *
 * Active services carry a LOT of identifiers a tech or office user might
 * search by — NBN AVC / location / NTD IDs, Starlink kit + account numbers,
 * SIM + mobile numbers, VoIP main numbers + porting numbers, and the serial
 * numbers of every installed AP / switch / NVR / hub / camera / alarm panel.
 *
 * Surfacing all of them here means typing an AVC ("AVC000195863320"), a kit
 * number, a SIM, or an access-point serial in global search jumps straight to
 * the right active service.
 */

import type { CustomerService } from '@/types/schema';

/**
 * The account number for a service can live in any of four places depending on
 * service type and how it was created (deal billing, Starlink email ingest,
 * Utilmate hand-off, or a manual edit). This resolves the first non-empty one
 * so display + search treat "the account number" consistently regardless of
 * where it was stored.
 *
 * Priority: top-level `accountNumber` → `metadata.accountNumber` →
 * `metadata.starlinkAccountNumber` → `utilmateHandoff.accountNumber`.
 */
export function getServiceAccountNumber(s: CustomerService): string | undefined {
  const m = s.metadata || {};
  const candidate =
    s.accountNumber ||
    m.accountNumber ||
    m.starlinkAccountNumber ||
    s.utilmateHandoff?.accountNumber;
  const trimmed = candidate?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * Every plain-text / identifier field on a customer service worth matching.
 * Returns a flat array (including spread arrays like AP serials + numbers to
 * port) ready to feed into the field scorer.
 */
export function customerServiceSearchFields(
  s: CustomerService,
): Array<string | undefined | null> {
  const m = s.metadata || {};
  return [
    // Core
    s.customerName,
    s.serviceType,
    s.planName,
    s.status,
    s.accountNumber,
    m.accountNumber, // generic account number stored in metadata
    s.ipAddress,
    // Address
    s.address,
    s.city,
    s.state,
    s.zipCode,
    // Utilmate hand-off join key
    s.utilmateHandoff?.accountNumber,
    s.utilmateHandoff?.siteIdentifier,

    // ── NBN (the AVC lives here) ──────────────────────────────────────────
    m.nbnServiceId, // AVC ID, e.g. AVC000195863320
    m.nbnLocationId, // LOC...
    m.ntdId,
    m.ntdPort,
    m.copperPairId,
    m.cTag,
    m.cpiId, // CPI...
    m.priId, // PRI...
    m.poiName,
    m.technology,
    m.utilmateId,
    m.utilmatePlanDescription,

    // ── Starlink ──────────────────────────────────────────────────────────
    m.kitNumber,
    m.starlinkAccountNumber, // ACC-XXXXXXX-XXXXX-XX
    m.starlinkAccountEmail,
    m.starlinkModel,
    // Deprecated serials — still searchable for legacy records.
    m.dishSerialNumber,
    m.routerSerialNumber,
    m.mountSerialNumber,

    // ── 4G / 5G ───────────────────────────────────────────────────────────
    m.carrier,
    m.simNumber,
    m.serviceNumber, // mobile number

    // ── VoIP ──────────────────────────────────────────────────────────────
    m.sasbossCustomerName,
    m.mainNumber,
    m.handsetBrand,
    ...(Array.isArray(m.numbersToPort) ? m.numbersToPort : []),

    // ── WiFi Extension ────────────────────────────────────────────────────
    m.accessPointBrand,
    m.accessPointModel,
    m.switchModel,
    m.switchSerial,
    ...(Array.isArray(m.accessPointSerials) ? m.accessPointSerials : []),

    // ── Smart Home ────────────────────────────────────────────────────────
    m.hubBrand,
    m.hubModel,
    m.hubSerialNumber,

    // ── Security ──────────────────────────────────────────────────────────
    m.cameraBrand,
    m.cameraModel,
    m.nvrModel,
    m.nvrSerial,
    m.panelSerialNumber,
    m.doorbellModel,

    // Common
    m.planName,
  ];
}
